import {useEffect} from 'react';
import {useSearchParams} from 'react-router-dom';

import LZString from 'lz-string';
import {Action} from 'redux';
import {useAppDispatch, useAppSelector} from 'src/store';
import {RootState} from 'src/store/store';
import {FILTER_KEY, getDecompressedFilters} from 'src/utils/getDecompressedFilters';

import {Icons} from '@pb/kit-component-icon';
import {Status, usePushNotification} from '@pb/kit-component-push-notification';

export const URL_LENGTH_LIMIT = 30000;
const CREATE_NODE_ID = 'url-limit';
const URL_LIMIT_MESSAGE = 'URL limit reached. To use and share the link successfully, ' +
    'please reduce the number of filtering parameters and try again.';

export const useFilters = <F>(
    getFilters: (state: RootState) => F,
    clearFilters: () => void,
    setFilters: (filters: F) => Action,
) => {
    const dispatch = useAppDispatch();
    const [searchParams, setSearchParams] = useSearchParams();
    const {onCreateNote} = usePushNotification();
    const filters = useAppSelector(getFilters);
    const filterParam = searchParams.get(FILTER_KEY);

    useEffect(() => {
        const params = {...Object.fromEntries(searchParams.entries())};
        params[FILTER_KEY] = LZString.compressToEncodedURIComponent(JSON.stringify(filters));

        setSearchParams(params, {replace: true});

        if (window.location.search.length >= URL_LENGTH_LIMIT) {
            console.error(URL_LIMIT_MESSAGE);

            onCreateNote({
                id: CREATE_NODE_ID,
                status: Status.WARNING,
                message: URL_LIMIT_MESSAGE,
                buttonProps: {icon: Icons.CLOSE},
                autoHideTimeout: 15000,
            });
        }
    }, [filters, onCreateNote, searchParams, setSearchParams]);

    useEffect(() => {
        if (filterParam) {
            const decompressedFilters = getDecompressedFilters<F>(searchParams);

            if (decompressedFilters) {
                dispatch(setFilters({...filters, ...decompressedFilters as unknown as F}));
            }

            return;
        }

        clearFilters();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {filters};
};
